/**
 * HandObjectClassifier — MobileNetV3-Small trained on hand-crop
 * none/pen/phone images (v2, 2026-05-22).
 *
 * v1 used 2-class (pen/phone) trained on landmark-contaminated screen
 * recordings (orange MediaPipe overlay visible on hand). Val acc 1.0
 * was overfit to the overlay; live inference uses RAW webcam with no
 * landmarks, causing domain mismatch.
 *
 * v2 fixes this with:
 *   1. HSV-based landmark masking + OpenCV inpaint during crop extract
 *   2. Added 'none' class (90 crops from empty-hand recordings)
 *   3. Per-class accuracy reporting during training
 * Best v2 val accuracy: 87.1 % (none 88, pen 100, phone 60).
 *
 * v3 (2026-05-22): expanded phone data 25 → 120 crops from raw 0522
 * 19/20-* MKV recordings, added focal loss (gamma=2) +
 * WeightedRandomSampler. Best val 88 % (none 94, pen 80-90, phone 87).
 * Phone class improvement: 60 → 87 % (relative +45 %).
 *
 * Model: MobileNetV3-Small (ImageNet pre-trained), 3-class linear head.
 * Input: 96×96 RGB, ImageNet-normalised.
 * Output: { none: prob, pen: prob, phone: prob } in class index order.
 *
 * Inference uses onnxruntime-web (WASM). First call lazy-loads the model;
 * subsequent calls reuse the cached InferenceSession. The class is
 * singleton-friendly — instantiate once and call classify() per frame.
 */

// `ort` (the import) gives us types AND a JSEP-bundled runtime that
// fails in browsers without JS Promise Integration. We swap the runtime
// by also requiring the WASM-only subpath and using THAT for actual
// API calls below. Type expressions still use `ort` for safety.
import * as ort from 'onnxruntime-web';
// eslint-disable-next-line @typescript-eslint/no-require-imports
const ortRt = require('onnxruntime-web/wasm') as typeof ort;

// Files served from FastAPI's /static mount.
const ASSET_BASE = '/static';

// Force single-threaded WASM backend — multi-threaded WASM requires
// SharedArrayBuffer which needs cross-origin isolation headers we don't
// control on the FastAPI server. Single-threaded just works.
// JSEP backend (default in ORT 1.26+) needs JS Promise Integration in
// the browser which is gated; explicitly fall back to the classic wasm
// bundle to avoid silent failures.
ortRt.env.wasm.wasmPaths = `${ASSET_BASE}/`;
ortRt.env.wasm.numThreads = 1;
ortRt.env.wasm.simd = true;
ortRt.env.wasm.proxy = false;
ortRt.env.logLevel = 'warning';

// v5 stays the default until v6 passes the operational (hand-ROI) gate.
// v6 = mixed-data retrain (own + LVIS crops): independent LVIS-crop audit
// 28.9% (v5) → 61.7% (v6), phone-collapse fixed — see ml_pen_phone/
// audit_v5_lvis.py + train_v6_mixed.py. `?clsModel=pen_phone_classifier_v6`
// for on-device A/B once the v6 onnx is placed in public/.
const CLS_MODEL =
  new URLSearchParams(window.location.search).get('clsModel') || 'pen_phone_classifier_v5';
const MODEL_URL = `${ASSET_BASE}/${CLS_MODEL}.onnx`;

/** Diagnostic capture — used to collect actual-deployment-distribution
 * data so we can fine-tune on raw webcam crops (closes the screen-rec
 * vs raw-webcam domain gap that hurts production accuracy).
 *
 * Usage in browser console:
 *   window.__captureHandCrop = 'pen'   // start continuously capturing
 *                                       // every successful inference
 *                                       // (rate-limited to 1/sec)
 *   // ... user holds pen, moves around, varies grip ...
 *   window.__captureHandCrop = null    // stop
 *   window.__downloadCaptures()        // download all as PNGs
 *   window.__clearCaptures()           // empty the buffer
 *
 * Captures are stored in-memory as { label, dataUrl, ts }. */
declare global {
  interface Window {
    __captureHandCrop?: 'pen' | 'phone' | 'none' | null;
    __captures?: Array<{ label: string; dataUrl: string; ts: number }>;
    __downloadCaptures?: () => void;
    __clearCaptures?: () => void;
    __lastCaptureTs?: number;
  }
}

if (typeof window !== 'undefined') {
  window.__captures = window.__captures || [];
  window.__downloadCaptures = () => {
    const buf = window.__captures || [];
    buf.forEach((c, i) => {
      const a = document.createElement('a');
      a.href = c.dataUrl;
      a.download = `handcrop_${c.label}_${c.ts}_${i}.png`;
      a.click();
    });
  };
  window.__clearCaptures = () => { window.__captures = []; };
}

// ImageNet normalisation — must match Python torchvision preprocess.
const MEAN = [0.485, 0.456, 0.406];
const STD  = [0.229, 0.224, 0.225];

export interface HandLandmark { x: number; y: number; z?: number }

export interface HandObjectResult {
  /** Top class: 'none' (empty hand), 'pen', or 'phone'. */
  dominant: 'none' | 'pen' | 'phone';
  /** Confidence of the dominant class (0..1). */
  confidence: number;
  noneProb: number;
  penProb: number;
  phoneProb: number;
  /** Hand bbox used (normalised image coords). */
  handBbox: { x: number; y: number; w: number; h: number };
}

const ZERO: HandObjectResult = {
  dominant: 'none',
  confidence: 0,
  noneProb: 0,
  penProb: 0,
  phoneProb: 0,
  handBbox: { x: 0, y: 0, w: 0, h: 0 },
};

let sessionPromise: Promise<ort.InferenceSession> | null = null;

export type ModelStatus = 'loading' | 'ready' | 'failed';
let modelStatus: ModelStatus = 'loading';
let modelError: string | null = null;
export function getModelStatus(): { status: ModelStatus; error: string | null } {
  return { status: modelStatus, error: modelError };
}

function loadSession(): Promise<ort.InferenceSession> {
  if (!sessionPromise) {
    modelStatus = 'loading';
    sessionPromise = ortRt.InferenceSession.create(MODEL_URL, {
      executionProviders: ['wasm'],
      graphOptimizationLevel: 'all',
    }).then(s => {
      modelStatus = 'ready';
      // eslint-disable-next-line no-console
      console.log('[HandObjectClassifier] ONNX session loaded', s.inputNames, '→', s.outputNames);
      return s;
    }).catch(err => {
      modelStatus = 'failed';
      modelError = err?.message ?? String(err);
      // eslint-disable-next-line no-console
      console.error('[HandObjectClassifier] session load FAILED:', err);
      throw err;
    });
  }
  return sessionPromise;
}

// Preload — call early so the first classify() is fast.
export function preloadHandObjectClassifier(): void {
  loadSession().catch(() => { /* will retry on first classify */ });
}

let lastResult: HandObjectResult = ZERO;
let inflight = false;

export function classifyHandObjectSync(): HandObjectResult { return lastResult; }

/**
 * Classify hand crop. Returns the most recent result synchronously and
 * kicks off a new inference if none in flight (single-flight pattern —
 * inference is async, so we don't block frame rendering).
 */
export async function classifyHandObject(
  landmarks: HandLandmark[] | null | undefined,
  video: HTMLVideoElement,
  offCanvas: HTMLCanvasElement,
): Promise<HandObjectResult> {
  if (!landmarks || landmarks.length < 21) {
    lastResult = ZERO;
    return ZERO;
  }
  if (inflight) return lastResult;

  const vw = video.videoWidth;
  const vh = video.videoHeight;
  if (!vw || !vh) return ZERO;

  let minX = 1, maxX = 0, minY = 1, maxY = 0;
  for (const p of landmarks) {
    if (p.x < minX) minX = p.x;
    if (p.x > maxX) maxX = p.x;
    if (p.y < minY) minY = p.y;
    if (p.y > maxY) maxY = p.y;
  }
  const padX = (maxX - minX) * 0.25;
  const padY = (maxY - minY) * 0.25;
  minX = Math.max(0, minX - padX);
  maxX = Math.min(1, maxX + padX);
  minY = Math.max(0, minY - padY);
  maxY = Math.min(1, maxY + padY);
  const handBbox = { x: minX, y: minY, w: maxX - minX, h: maxY - minY };
  if (handBbox.w < 0.04 || handBbox.h < 0.04) {
    lastResult = ZERO;
    return ZERO;
  }

  if (offCanvas.width !== 96 || offCanvas.height !== 96) {
    offCanvas.width = 96;
    offCanvas.height = 96;
  }
  const ctx = offCanvas.getContext('2d', { willReadFrequently: true });
  if (!ctx) return ZERO;
  ctx.drawImage(
    video,
    handBbox.x * vw, handBbox.y * vh,
    handBbox.w * vw, handBbox.h * vh,
    0, 0, 96, 96,
  );
  const img = ctx.getImageData(0, 0, 96, 96);

  // Capture mode — continuously stash hand crops with a rate limit
  // of one per second, so the buffer doesn't grow unbounded.
  if (typeof window !== 'undefined' && window.__captureHandCrop) {
    const now = performance.now();
    const lastTs = window.__lastCaptureTs ?? 0;
    if (now - lastTs >= 1000) {
      window.__lastCaptureTs = now;
      const label = window.__captureHandCrop;
      const dataUrl = offCanvas.toDataURL('image/png');
      (window.__captures ?? []).push({ label, dataUrl, ts: Date.now() });
    }
  }

  // CHW float32, ImageNet-normalised
  const input = new Float32Array(3 * 96 * 96);
  for (let i = 0; i < 96 * 96; i++) {
    const j = i * 4;
    input[i]             = (img.data[j]     / 255 - MEAN[0]) / STD[0];
    input[96 * 96 + i]   = (img.data[j + 1] / 255 - MEAN[1]) / STD[1];
    input[2 * 96 * 96 + i] = (img.data[j + 2] / 255 - MEAN[2]) / STD[2];
  }

  inflight = true;
  try {
    const session = await loadSession();
    const tensor = new ortRt.Tensor('float32', input, [1, 3, 96, 96]);
    const output = await session.run({ input: tensor });
    const probs = output.probs.data as Float32Array;
    // Class index: { none: 0, pen: 1, phone: 2 } — matches train_v2.py
    const noneProb  = probs[0];
    const penProb   = probs[1];
    const phoneProb = probs[2];
    const max = Math.max(noneProb, penProb, phoneProb);
    let dominant: 'none' | 'pen' | 'phone';
    if (max === phoneProb) dominant = 'phone';
    else if (max === penProb) dominant = 'pen';
    else dominant = 'none';
    lastResult = {
      dominant,
      confidence: max,
      noneProb, penProb, phoneProb,
      handBbox,
    };
    return lastResult;
  } catch (err) {
    // eslint-disable-next-line no-console
    console.error('[HandObjectClassifier] inference error:', err);
    return lastResult;
  } finally {
    inflight = false;
  }
}
